import React, { useState, useEffect } from 'react';
import { CreditCard, Smartphone, CheckCircle, ArrowLeft, Lock, AlertCircle } from 'lucide-react';


/**
 * Reusable Payment Gateway Component
 * 
 * @param {string} amount - Base amount (without GST)
 * @param {Object} userDetails - User billing details { name, email, phone }
 * @param {Function} onSuccess - Callback on successful payment
 * @param {Function} onClose - Callback to close payment modal
 * @param {string} paymentType - 'purohit_unlock' | 'user_subscription'
 * @param {Object} metadata - Additional data (e.g., purohitIds, subscriptionPlan)
 * @param {Function} onPaymentSubmit - Custom payment API handler
 * @param {boolean} isProcessing - External processing state
 * @param {string} title - Custom payment modal title
 * @param {string} description - Custom description text
 */
export default function PaymentGateway({
  amount = '999',
  userDetails = {
    name: 'John Doe',
    email: 'john@example.com',
    phone: '+91 98765 43210'
  },
  onSuccess,
  onClose,
  paymentType = 'user_subscription',
  metadata = {},
  onPaymentSubmit,
  isProcessing: externalProcessing = false,
  title = 'Complete Your Payment',
  description = 'Choose your preferred payment method below'
}) {
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Card Details
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  
  // UPI Details
  const [upiId, setUpiId] = useState('');
  
  // Validation Errors
  const [cardNameError, setCardNameError] = useState('');
  const [upiIdError, setUpiIdError] = useState('');
  
  // Transaction Details
  const [transactionId, setTransactionId] = useState('');

  // Calculate total with GST
  const baseAmount = Number(amount);
  const gstAmount = (baseAmount * 0.18).toFixed(2);
  const totalAmount = (baseAmount * 1.18).toFixed(2);

  // Auto-redirect after success
  useEffect(() => {
    if (paymentSuccess && onSuccess) {
      const timer = setTimeout(() => {
        onSuccess({ 
          transactionId, 
          amount: totalAmount, 
          paymentMethod,
          metadata 
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [paymentSuccess, onSuccess, transactionId, totalAmount, paymentMethod, metadata]);

  // ============= FORMATTING FUNCTIONS =============
  const formatCardNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    const groups = numbers.match(/.{1,4}/g);
    return groups ? groups.join(' ') : numbers;
  };

  const formatExpiryDate = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length >= 2) {
      return numbers.slice(0, 2) + '/' + numbers.slice(2, 4);
    }
    return numbers;
  };

  const generatePaymentId = () => {
    return 'PAY_' + Math.random().toString(36).substr(2, 9).toUpperCase() + Date.now().toString(36).toUpperCase();
  };

  // ============= CHANGE HANDLERS =============
  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 16) {
      setCardNumber(formatted);
    }
  };

  const handleCardNameChange = (e) => {
    const value = e.target.value;
    const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
    setCardName(filteredValue.toUpperCase());
    
    if (filteredValue.trim().length === 0) {
      setCardNameError('Cardholder name is required');
    } else if (filteredValue.trim().length < 3) {
      setCardNameError('Name must be at least 3 characters');
    } else {
      setCardNameError('');
    }
  };

  const handleUpiIdChange = (e) => {
    const value = e.target.value.toLowerCase();
    
    if (value.length > 50) {
      setUpiIdError("UPI ID must be less than 50 characters");
      return;
    }

    setUpiId(value);
    
    if (value.trim().length === 0) {
      setUpiIdError('UPI ID is required');
    } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/.test(value)) {
      if (!value.includes('@')) {
        setUpiIdError('UPI ID must contain @');
      } else if (value.split('@')[0].length === 0) {
        setUpiIdError('Enter valid username before @');
      } else if (value.split('@')[1].length === 0) {
        setUpiIdError('Enter valid payment provider after @');
      } else {
        setUpiIdError('Invalid UPI ID format');
      }
    } else {
      setUpiIdError('');
    }
  };

  const handleExpiryChange = (e) => {
    const formatted = formatExpiryDate(e.target.value);
    if (formatted.replace(/\//g, '').length <= 4) {
      setExpiryDate(formatted);
    }
  };

  const handleCvvChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      setCvv(value);
    }
  };

  const handlePaymentMethodToggle = (method) => {
    setPaymentMethod(paymentMethod === method ? null : method);
  };

  // ============= VALIDATION =============
  const validatePaymentDetails = () => {
    if (paymentMethod === 'card') {
      if (!cardNumber || !cardName || !expiryDate || !cvv) {
        return { valid: false, message: 'Please fill in all card details' };
      }
      if (cardNameError) {
        return { valid: false, message: 'Please enter a valid cardholder name' };
      }
      if (cardNumber.replace(/\s/g, '').length !== 16) {
        return { valid: false, message: 'Please enter a valid 16-digit card number' };
      }
      if (expiryDate.replace(/\//g, '').length !== 4) {
        return { valid: false, message: 'Please enter a valid expiry date (MM/YY)' };
      }
      if (cvv.length < 3) {
        return { valid: false, message: 'Please enter a valid CVV' };
      }
    } else if (paymentMethod === 'upi') {
      if (!upiId) {
        return { valid: false, message: 'Please enter your UPI ID' };
      }
      if (upiIdError) {
        return { valid: false, message: 'Please enter a valid UPI ID' };
      }
    } else {
      return { valid: false, message: 'Please select a payment method' };
    }
    
    return { valid: true };
  };

  // ============= PAYMENT HANDLER =============
  const handlePayment = async () => {
    const validation = validatePaymentDetails();
    
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    setProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      const paymentId = generatePaymentId();
      setTransactionId(paymentId);

      const paymentData = {
        paymentId,
        amount: Number(totalAmount),
        paymentMethod,
        paymentType,
        userDetails,
        ...metadata
      };

      // Call custom payment submission handler if provided
      if (onPaymentSubmit) {
        await onPaymentSubmit(paymentData);
      }

      setPaymentSuccess(true);

    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  // ============= RENDER STATES =============
  
  // External Processing
  if (externalProcessing) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)'}}>
        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto mb-6">
            <div className="animate-spin rounded-full h-24 w-24 border-4 border-orange-200 border-t-orange-600 shadow-lg"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Processing...</h2>
          <p className="text-gray-600 mb-4">Please wait while we complete your request</p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Lock size={16} />
            <span>Secure process in progress</span>
          </div>
        </div>
      </div>
    );
  }

  // Payment Success
  if (paymentSuccess) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-100 flex items-center justify-center z-50 px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
            <CheckCircle className="w-14 h-14 text-white" strokeWidth={2.5} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Payment Successful!</h2>
          <p className="text-gray-600 text-lg mb-6">
            Your payment of <span className="font-bold text-green-600">₹{totalAmount}</span> has been processed successfully.
          </p>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-5 mb-6">
            <p className="text-sm text-green-900 mb-2">
              <strong>Transaction ID:</strong> {transactionId}
            </p>
            <p className="text-sm text-green-900">
              <strong>Date:</strong> {new Date().toLocaleString()}
            </p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-green-600"></div>
            <p className="text-sm text-gray-500">Redirecting...</p>
          </div>
        </div>
      </div>
    );
  }

  // Processing Payment
  if (processing) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)'}}>
        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto mb-6">
            <div className="animate-spin rounded-full h-24 w-24 border-4 border-orange-200 border-t-orange-600 shadow-lg"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Processing Payment...</h2>
          <p className="text-gray-600 mb-4">Please do not refresh or close this window</p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Lock size={16} />
            <span>Secure transaction in progress</span>
          </div>
        </div>
      </div>
    );
  }

  // ============= MAIN PAYMENT UI =============
  return (
    <div className="fixed inset-0 bg-white overflow-y-auto z-50">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-3xl shadow-xl p-6 mb-6 border border-orange-400">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={onClose}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-all"
              >
                <ArrowLeft size={20} />
                <span className="text-sm font-semibold">Back</span>
              </button>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-200">
                <Lock size={16} className="text-green-600" />
                <span className="text-sm font-semibold text-green-700">Secure Payment</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
              {title}
            </h1>
            <p className="text-gray-600 text-lg">{description}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Payment Methods */}
            <div className="md:col-span-2 space-y-5">
              {/* Card Payment */}
              <div
                className={`bg-white rounded-3xl shadow-xl p-6 cursor-pointer transition-all border-2 ${
                  paymentMethod === 'card' ? 'border-orange-500 shadow-orange-200' : 'border-orange-300 hover:shadow-2xl'
                }`}
                onClick={() => handlePaymentMethodToggle('card')}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <CreditCard className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">Credit/Debit Card</h3>
                      <p className="text-sm text-gray-500">Visa, Mastercard, Rupay</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    paymentMethod === 'card' ? 'border-orange-500 bg-orange-500' : 'border-orange-300 bg-white'
                  }`}>
                    {paymentMethod === 'card' && <div className="w-3 h-3 bg-white rounded-full"></div>}
                  </div>
                </div>

                {paymentMethod === 'card' && (
                  <div className="space-y-4 pt-5 border-t-2 border-gray-100">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Card Number</label>
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={handleCardNumberChange}
                        placeholder="1234 5678 9012 3456"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Cardholder Name</label>
                      <input
                        type="text"
                        value={cardName}
                        onChange={handleCardNameChange}
                        placeholder="JOHN DOE"
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 outline-none transition-all ${
                          cardNameError 
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                            : 'border-gray-200 focus:ring-orange-500 focus:border-orange-500'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {cardNameError && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {cardNameError}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date</label>
                        <input
                          type="text"
                          value={expiryDate}
                          onChange={handleExpiryChange}
                          placeholder="MM/YY"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">CVV</label>
                        <input
                          type="password"
                          value={cvv}
                          onChange={handleCvvChange}
                          placeholder="123"
                          maxLength={4}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* UPI Payment */}
              <div
                className={`bg-white rounded-3xl shadow-xl p-6 cursor-pointer transition-all border-2 ${
                  paymentMethod === 'upi' ? 'border-orange-500 shadow-orange-200' : 'border-orange-300 hover:shadow-2xl'
                }`}
                onClick={() => handlePaymentMethodToggle('upi')}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-pink-600 flex items-center justify-center shadow-lg">
                      <Smartphone className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">UPI Payment</h3>
                      <p className="text-sm text-gray-500">Google Pay, PhonePe, Paytm</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    paymentMethod === 'upi' ? 'border-orange-500 bg-orange-500' : 'border-orange-300 bg-white'
                  }`}>
                    {paymentMethod === 'upi' && <div className="w-3 h-3 bg-white rounded-full"></div>}
                  </div>
                </div>

                {paymentMethod === 'upi' && (
                  <div className="space-y-4 pt-5 border-t-2 border-gray-100">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">UPI ID</label>
                      <input
                        type="text"
                        value={upiId}
                        onChange={handleUpiIdChange}
                        placeholder="yourname@upi"
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 outline-none transition-all ${
                          upiIdError 
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                            : 'border-gray-200 focus:ring-orange-500 focus:border-orange-500'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {upiIdError && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {upiIdError}
                        </p>
                      )}
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4">
                      <p className="text-sm text-purple-900 flex items-start gap-3">
                        <AlertCircle size={18} className="mt-0.5 flex-shrink-0 text-purple-600" />
                        <span>You'll receive a notification on your UPI app to approve this payment</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-3xl shadow-xl p-6 sticky top-7 border border-orange-400">
                <h3 className="text-xl font-bold text-gray-900 mb-5 pb-3 border-b-2 border-green-500">Order Summary</h3>
                <div className="space-y-3 mb-5">
                  <div className="flex justify-between text-base">
                    <span className="text-gray-600">Base Amount</span>
                    <span className="font-semibold text-gray-900">₹{baseAmount}</span>
                  </div>
                  <div className="flex justify-between text-base">
                    <span className="text-gray-600">GST (18%)</span>
                    <span className="font-semibold text-gray-900">₹{gstAmount}</span>
                  </div>
                  <div className="border-t-2 pt-4 flex justify-between items-center">
                    <span className="font-bold text-gray-900 text-lg">Total Amount</span>
                    <span className="font-bold text-orange-600 text-2xl">₹{totalAmount}</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-4 mb-6">
                  <h4 className="font-bold text-gray-900 text-sm mb-3">Billing Details</h4>
                  <p className="text-sm text-gray-700 mb-2"><strong>Name:</strong> {userDetails.name}</p>
                  <p className="text-sm text-gray-700 mb-2"><strong>Email:</strong> {userDetails.email}</p>
                  <p className="text-sm text-gray-700"><strong>Phone:</strong> {userDetails.phone}</p>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={!paymentMethod}
                  className={`w-full py-4 rounded-xl font-bold text-white text-lg transition-all ${
                    paymentMethod
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:shadow-2xl hover:shadow-orange-300 transform hover:-translate-y-1'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  Pay ₹{totalAmount}
                </button>

                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Lock size={14} />
                  <span>Secured by 256-bit SSL encryption</span>
                </div>
              </div>
            </div>
          </div>

          {/* Security Badges */}
          <div className="mt-6 bg-white rounded-3xl shadow-xl p-6 border border-gray-100">
            <div className="flex flex-wrap items-center justify-center gap-8 text-gray-500">
              <div className="flex items-center gap-2">
                <Lock size={18} />
                <span className="text-sm font-medium">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={18} />
                <span className="text-sm font-medium">100% Safe</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle size={18} />
                <span className="text-sm font-medium">PCI DSS Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}